// Insurance tab rebuild — "File a claim" modal. Mirrors the
// AddInsurancePolicyModal patterns (Modal + toast + mutateAsync).

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useLoanInsurance } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useFileInsuranceClaim } from "@/hooks/portfolioAdmin/useInsuranceClaims";
import type { FileInsuranceClaimRequest } from "@/api/portfolioAdmin/insuranceClaimsApi";

interface FileClaimModalProps {
  loanId: string;
  open: boolean;
  onClose: () => void;
}

export function FileClaimModal({ loanId, open, onClose }: FileClaimModalProps) {
  const toast = useToast();
  const { data: policies } = useLoanInsurance(loanId);
  const file = useFileInsuranceClaim(loanId);

  const [policyId, setPolicyId] = useState("");
  const [dateOfLoss, setDateOfLoss] = useState("");
  const [description, setDescription] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [amountClaimed, setAmountClaimed] = useState("");

  const onSubmit = async () => {
    if (!policyId) {
      toast.error("Select the policy the claim is filed against.");
      return;
    }
    if (!dateOfLoss) {
      toast.error("Date of loss is required.");
      return;
    }
    if (!description.trim()) {
      toast.error("Description is required.");
      return;
    }
    const body: FileInsuranceClaimRequest = {
      insurancePolicyId: policyId,
      dateOfLoss,
      description: description.trim(),
      claimNumber: claimNumber.trim() || null,
      amountClaimed: amountClaimed ? Number(amountClaimed) : null,
    };
    try {
      await file.mutateAsync(body);
      toast.success("Claim filed.");
      setPolicyId("");
      setDateOfLoss("");
      setDescription("");
      setClaimNumber("");
      setAmountClaimed("");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not file claim.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="File a claim"
      description="Records the claim against an existing policy. Advance its status from the claim drawer as the carrier responds."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={file.isPending}
            data-testid="claim-file-submit"
          >
            File claim
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-space-3" data-testid="claim-file-form">
        <label className="flex flex-col gap-space-1 text-caption col-span-2">
          <span className="text-gray-mid uppercase tracking-label">
            Policy <span className="text-status-error">*</span>
          </span>
          <select
            className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
            value={policyId}
            onChange={(e) => setPolicyId(e.target.value)}
            data-testid="claim-file-policy"
          >
            <option value="">Select a policy…</option>
            {(policies ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.policyType}
                {p.carrierName ? ` — ${p.carrierName}` : ""}
                {p.policyNumber ? ` (#${p.policyNumber})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Date of loss <span className="text-status-error">*</span>
          </span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={dateOfLoss}
            onChange={(e) => setDateOfLoss(e.target.value)}
            required
            data-testid="claim-file-dateOfLoss"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Claim #</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
            value={claimNumber}
            onChange={(e) => setClaimNumber(e.target.value)}
            data-testid="claim-file-claimNumber"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption col-span-2">
          <span className="text-gray-mid uppercase tracking-label">
            Description <span className="text-status-error">*</span>
          </span>
          <textarea
            rows={3}
            className="w-full border border-border-light rounded-md p-space-2 text-small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            data-testid="claim-file-description"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Amount claimed ($)
          </span>
          <input
            type="number"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={amountClaimed}
            onChange={(e) => setAmountClaimed(e.target.value)}
            data-testid="claim-file-amountClaimed"
          />
        </label>
      </div>
    </Modal>
  );
}
